/**
 * Shared "Bağlantı ile paylaş" dialog.
 *
 * Renders a Radix Dialog on top of any construction file. The parent
 * only supplies the file (or null when closed) and the on-close handler
 * — the dialog owns its own list state, TTL selection and clipboard
 * side-effects. Used by both the per-project files tab
 * (`CopConstructionTrackingDetail`) and the tenant-wide browser
 * (`CopFilesConstruction`) so the two views stay in sync when we tweak
 * behaviour.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Share2,
  Loader2,
  Clock,
  Copy,
  Check,
  Trash2,
} from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { constructionApi } from "../../lib/copApi";

export default function ShareLinkDialog({ file, onClose }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [ttlDays, setTtlDays] = useState(7);

  useEffect(() => {
    let cancelled = false;
    if (!file) {
      setLinks([]);
      return () => {};
    }
    setLoading(true);
    (async () => {
      try {
        const data = await constructionApi.listShareLinks(file.id);
        if (!cancelled) setLinks(data || []);
      } catch {
        if (!cancelled) toast.error("Paylaşım linkleri yüklenemedi");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [file]);

  const shareUrlFor = (token) => `${window.location.origin}/share/${token}`;

  const createLink = async () => {
    if (!file) return;
    setCreating(true);
    try {
      const link = await constructionApi.createShareLink(file.id, {
        expires_in_days: ttlDays,
      });
      setLinks((prev) => [link, ...prev]);
      // Auto-copy so the user can paste into an email right away.
      try {
        await navigator.clipboard.writeText(shareUrlFor(link.token));
        toast.success("Link kopyalandı");
      } catch {
        toast.success("Link oluşturuldu");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Link oluşturulamadı";
      toast.error(String(msg));
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (linkId) => {
    try {
      await constructionApi.revokeShareLink(linkId);
      setLinks((prev) =>
        prev.map((l) => (l.id === linkId ? { ...l, is_revoked: true } : l))
      );
      toast.success("Bağlantı iptal edildi");
    } catch {
      toast.error("İptal edilemedi");
    }
  };

  const active = links.filter((l) => !l.is_revoked && !l.is_expired);
  const inactive = links.filter((l) => l.is_revoked || l.is_expired);

  return (
    <Dialog open={!!file} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="cf-share-dialog"
        className="max-w-lg gap-0 overflow-hidden p-0"
      >
        {file && (
          <>
            <div className="flex items-center gap-3 border-b border-zinc-200 px-5 py-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-100 text-violet-700">
                <Share2 className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-sm font-semibold text-zinc-900">
                  Bağlantı ile paylaş
                </DialogTitle>
                <div className="truncate text-[11px] text-zinc-500">
                  {file.original_filename}
                </div>
              </div>
            </div>

            <div className="border-b border-zinc-100 bg-zinc-50 px-5 py-4">
              <div className="flex items-center gap-2">
                <div className="flex flex-1 items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2">
                  <Clock className="h-3.5 w-3.5 text-zinc-400" />
                  <span className="text-[12px] text-zinc-600">Süre:</span>
                  <select
                    value={ttlDays}
                    onChange={(e) => setTtlDays(Number(e.target.value))}
                    data-testid="cf-share-ttl"
                    className="flex-1 border-0 bg-transparent text-[13px] font-medium text-zinc-900 outline-none"
                  >
                    <option value={1}>1 gün</option>
                    <option value={3}>3 gün</option>
                    <option value={7}>7 gün</option>
                    <option value={14}>14 gün</option>
                    <option value={30}>30 gün</option>
                    <option value={90}>90 gün</option>
                  </select>
                </div>
                <Button
                  onClick={createLink}
                  disabled={creating}
                  data-testid="cf-share-create"
                  className="bg-violet-600 text-white hover:bg-violet-700"
                >
                  {creating ? (
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  ) : (
                    <Share2 className="mr-1.5 h-4 w-4" />
                  )}
                  Yeni link
                </Button>
              </div>
              <p className="mt-2 text-[11px] text-zinc-500">
                Link sahibi giriş yapmadan dosyayı indirebilir. Süre dolduğunda
                otomatik olarak devre dışı kalır.
              </p>
            </div>

            <div className="max-h-80 overflow-y-auto px-5 py-4">
              {loading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
                </div>
              ) : links.length === 0 ? (
                <div className="py-6 text-center text-[12px] text-zinc-400">
                  Henüz bağlantı yok.
                </div>
              ) : (
                <>
                  {active.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                        Aktif ({active.length})
                      </div>
                      {active.map((l) => (
                        <ShareLinkRow
                          key={l.id}
                          link={l}
                          shareUrl={shareUrlFor(l.token)}
                          onRevoke={() => revoke(l.id)}
                        />
                      ))}
                    </div>
                  )}
                  {inactive.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                        Pasif ({inactive.length})
                      </div>
                      {inactive.map((l) => (
                        <ShareLinkRow
                          key={l.id}
                          link={l}
                          shareUrl={shareUrlFor(l.token)}
                          onRevoke={null}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ShareLinkRow({ link, shareUrl, onRevoke }) {
  const [copied, setCopied] = useState(false);
  const inactiveReason = link.is_revoked
    ? "İptal"
    : link.is_expired
      ? "Süresi doldu"
      : null;

  // "N gün / N saat sonra dolar" — kept inline because the app's shared
  // `formatRelative` helper is for past timestamps only.
  const remaining = (() => {
    if (!link.expires_at) return "";
    const t = new Date(link.expires_at).getTime();
    if (Number.isNaN(t)) return "";
    const diff = t - Date.now();
    if (diff <= 0) return "";
    const hr = Math.round(diff / 3600_000);
    if (hr < 24) return `${hr} saat`;
    return `${Math.round(hr / 24)} gün`;
  })();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Kopyalanamadı");
    }
  };

  return (
    <div
      data-testid={`cf-share-row-${link.id}`}
      className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${
        inactiveReason
          ? "border-zinc-200 bg-zinc-50 opacity-70"
          : "border-zinc-200 bg-white"
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate font-mono text-[11px] text-zinc-700">
          {shareUrl}
        </div>
        <div className="mt-0.5 text-[10px] text-zinc-500">
          {link.created_by_name || "—"} · {link.download_count} indirme
          {remaining && !inactiveReason && (
            <> · {remaining} sonra dolar</>
          )}
          {inactiveReason && <> · {inactiveReason}</>}
        </div>
      </div>
      {!inactiveReason && (
        <>
          <button
            type="button"
            onClick={copy}
            data-testid={`cf-share-copy-${link.id}`}
            className="flex h-7 w-7 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
            aria-label="Kopyala"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-emerald-600" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
          {onRevoke && (
            <button
              type="button"
              onClick={onRevoke}
              data-testid={`cf-share-revoke-${link.id}`}
              className="flex h-7 w-7 items-center justify-center rounded-md text-rose-500 hover:bg-rose-50"
              aria-label="İptal et"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </>
      )}
    </div>
  );
}
